import {
  type FieldConfig,
  type FilterNode,
  type SearchRequestInput,
  type SearchResponse,
  TICKET_KEY_PATTERN,
} from '@servicetim/shared';
import { useCallback } from 'react';
import { portalPath, useApiContext } from '../../../api/client';
import type { FieldControlProps } from '../field-control';
import type { OptionLoader } from '../types';
import { AsyncPicker } from './async-picker';

type TicketLookupConfig = Extract<FieldConfig, { type: 'ticket_lookup' }>;

/**
 * Default ticket loader over `POST /v1/tickets/search`: a query that looks like a key
 * (`IT-42`) matches by key, anything else by summary; the field config narrows projects /
 * issue types.
 */
export function useTicketOptionLoader(config?: TicketLookupConfig): OptionLoader {
  const { request } = useApiContext();
  const projectIds = config?.allowedProjectIds ?? null;
  const issueTypeIds = config?.allowedIssueTypeIds ?? null;
  return useCallback(
    async (query, signal) => {
      const q = query.trim();
      const clauses: FilterNode[] = [];
      if (projectIds && projectIds.length > 0) {
        clauses.push({ field: 'project_id', op: 'in', value: projectIds });
      }
      if (issueTypeIds && issueTypeIds.length > 0) {
        clauses.push({ field: 'issue_type_id', op: 'in', value: issueTypeIds });
      }
      if (q.length > 0) {
        clauses.push(
          TICKET_KEY_PATTERN.test(q.toUpperCase())
            ? { field: 'key', op: 'eq', value: q.toUpperCase() }
            : { field: 'summary', op: 'contains', value: q },
        );
      }
      const filter =
        clauses.length === 0 ? undefined : clauses.length === 1 ? clauses[0] : { and: clauses };
      const body: SearchRequestInput = {
        filter,
        sort: [{ field: 'updated_at', dir: 'desc' }],
        select: ['id', 'key', 'summary'],
        page: { size: 20 },
      };
      const result = await request<SearchResponse>('POST', portalPath('/tickets/search'), {
        body,
        signal,
      });
      return result.items.map((t) => ({ id: t.id, label: t.key, description: t.summary ?? null }));
    },
    [request, projectIds, issueTypeIds],
  );
}

/** `ticket_lookup`: async picker by key or summary. */
export function TicketLookupControl(props: FieldControlProps) {
  const { field, value, onChange, id, disabled, invalid, describedBy, displayLabel, pickers } =
    props;
  const config = field.config?.type === 'ticket_lookup' ? field.config : undefined;
  const defaultLoader = useTicketOptionLoader(config);
  return (
    <AsyncPicker
      id={id}
      value={typeof value === 'string' ? value : undefined}
      onChange={onChange}
      load={pickers?.tickets ?? defaultLoader}
      displayLabel={displayLabel}
      placeholder={field.placeholder ?? 'Find a ticket…'}
      searchPlaceholder="Key (IT-42) or summary…"
      disabled={disabled || field.readOnly}
      invalid={invalid}
      required={field.required}
      describedBy={describedBy}
    />
  );
}
